import { useEffect, useState, type FormEvent } from "react";
import {
  VEHICLE_CLASSES,
  VEHICLE_CLASS_LABELS,
  type VehicleClass,
  type Vehicle,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { useToastStore } from "../../../core/stores/toast";
import { createVehicle, updateVehicle, type VehicleInput } from "../lib/api";
import { VehicleIdentityFields } from "./vehicle-identity";

const FORM_ID = "atelier-vehicle-form";

export interface VehicleFormState {
  pricingClass: string;
  make: string;
  model: string;
  year: string;
  vin: string;
  plate: string;
  mileage: string;
  color: string;
  notes: string;
}

export function vehicleFormFrom(v?: Vehicle): VehicleFormState {
  return {
    pricingClass: v?.pricingClass ?? "",
    make: v?.make ?? "",
    model: v?.model ?? "",
    year: v?.year ? String(v.year) : "",
    vin: v?.vin ?? "",
    plate: v?.plate ?? "",
    mileage: v?.mileage !== null && v?.mileage !== undefined ? String(v.mileage) : "",
    color: v?.color ?? "",
    notes: v?.notes ?? "",
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);
const intOrNull = (s: string) => {
  const n = Number(s);
  return s.trim() && Number.isInteger(n) ? n : null;
};

/** Charge utile API ; null si marque ou modèle manquent. */
export function vehicleInputFrom(f: VehicleFormState): VehicleInput | null {
  if (!f.make.trim() || !f.model.trim()) return null;
  return {
    pricingClass: (f.pricingClass || null) as VehicleClass | null,
    make: f.make.trim(),
    model: f.model.trim(),
    year: intOrNull(f.year),
    vin: orNull(f.vin)?.toUpperCase() ?? null,
    plate: orNull(f.plate)?.toUpperCase() ?? null,
    mileage: intOrNull(f.mileage),
    color: orNull(f.color),
    notes: orNull(f.notes),
  };
}

/** Champs d'un véhicule, réutilisés par la modal et par la création rapide dans le rendez-vous. */
export function VehicleFields({
  form,
  onChange,
  autoFocus,
}: {
  form: VehicleFormState;
  onChange: (f: VehicleFormState) => void;
  autoFocus?: boolean;
}) {
  const set = <K extends keyof VehicleFormState>(key: K, value: string) =>
    onChange({ ...form, [key]: value });
  return (
    <>
      <div className="form-section__grid">
        <Field label="Catégorie tarifaire">
          <Select value={form.pricingClass} onChange={(e) => set("pricingClass", e.target.value)}>
            <option value="">À préciser</option>
            {VEHICLE_CLASSES.map((c) => (
              <option key={c} value={c}>
                {VEHICLE_CLASS_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
        <VehicleIdentityFields
          value={{ year: form.year, make: form.make, model: form.model }}
          onChange={(patch) => onChange({ ...form, ...patch })}
          autoFocus={autoFocus}
        />
        <Field label="Immatriculation">
          <Input
            value={form.plate}
            onChange={(e) => set("plate", e.target.value)}
            placeholder="ex : ABC 123"
          />
        </Field>
        <Field label="NIV" hint="17 caractères.">
          <Input value={form.vin} onChange={(e) => set("vin", e.target.value)} maxLength={17} />
        </Field>
        <Field label="Kilométrage">
          <Input
            inputMode="numeric"
            value={form.mileage}
            onChange={(e) => set("mileage", e.target.value)}
          />
        </Field>
        <Field label="Couleur">
          <Input value={form.color} onChange={(e) => set("color", e.target.value)} />
        </Field>
      </div>
      <div className="form-section__grid form-section__grid--full">
        <Field label="Notes">
          <Textarea
            className="textarea--sm"
            rows={2}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
      </div>
    </>
  );
}

interface VehicleFormModalProps {
  open: boolean;
  clientId: string;
  vehicle?: Vehicle;
  onClose: () => void;
  onSaved: (vehicle: Vehicle) => void;
}

export function VehicleFormModal({
  open,
  clientId,
  vehicle,
  onClose,
  onSaved,
}: VehicleFormModalProps) {
  const push = useToastStore((s) => s.push);
  const [form, setForm] = useState<VehicleFormState>(() => vehicleFormFrom(vehicle));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(vehicleFormFrom(vehicle));
  }, [open, vehicle]);

  const input = vehicleInputFrom(form);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input || saving) return;
    setSaving(true);
    try {
      const saved = vehicle
        ? await updateVehicle(vehicle.id, input)
        : await createVehicle(clientId, input);
      push("success", vehicle ? "Véhicule mis à jour" : "Véhicule ajouté");
      onSaved(saved);
    } catch {
      push("danger", "Enregistrement impossible", "Vérifiez les champs saisis.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={vehicle ? "Modifier le véhicule" : "Nouveau véhicule"}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={saving || !input}>
            {saving ? "Enregistrement…" : vehicle ? "Enregistrer" : "Ajouter le véhicule"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="atelier-form" onSubmit={submit}>
        <VehicleFields form={form} onChange={setForm} autoFocus />
      </form>
    </Modal>
  );
}
